/**
 * Centralized help content for all screens and components
 * Each page/component has structured help content organized by sections
 */

import {
  CAPTURE_SETUP_HELP_DISTANCE_TEXT,
  CAPTURE_SETUP_HELP_HEIGHT_TEXT,
} from '@/components/posture-analysis/lib/capture-setup-standard';

export interface HelpSection {
  title: string;
  content: string;
  subsections?: HelpSubsection[];
  type?: 'default' | 'technical' | 'tip'; // 'technical' for academic/technical deep-dive sections; 'tip' for tips
}

export interface HelpSubsection {
  title: string;
  content: string;
  type?: 'info' | 'warning' | 'tip' | 'success' | 'technical';
  icon?: string;
}

export interface HelpContent {
  title: string;
  description: string;
  sections: HelpSection[];
}

export const HELP_CONTENT: Record<string, HelpContent> = {
  // Main Pages
  dashboard: {
    title: "Dashboard Help",
    description: "Your personal overview and quick access to key features",
    sections: [
      {
        title: "Overview",
        content: "The Dashboard provides a comprehensive view of your practice activity, including recent clients, assessment statistics, and quick action buttons. This is your home base for managing your biomechanics practice.",
      },
      {
        title: "Quick Actions",
        content: "Access frequently used features directly from the dashboard:",
        subsections: [
          {
            title: "New Client",
            content: "Click to add a new client to your database. You'll be taken to a multi-step form to capture client information.",
            type: "info",
            icon: "➕"
          },
          {
            title: "New Assessment",
            content: "Start a new SOP (Standard Operating Procedure) assessment for a selected client. Make sure you have a client selected first.",
            type: "info",
            icon: "📋"
          },
          {
            title: "Posture Analysis",
            content: "Launch the AI-powered posture analysis tool to analyze client posture from photos.",
            type: "info",
            icon: "📸"
          }
        ]
      },
      {
        title: "Recent Activity",
        content: "View your most recent client interactions and assessments. Click on any item to view details or continue work.",
      },
      {
        title: "Statistics",
        content: "Track your practice metrics including total clients, assessments completed, and active treatment plans.",
      }
    ]
  },

  clients: {
    title: "Clients Management Help",
    description: "Manage your client database efficiently",
    sections: [
      {
        title: "Client List Overview",
        content: "This page displays all clients in your practice. The list shows key information including client name, date added, last activity, and quick action buttons.",
      },
      {
        title: "Adding a New Client",
        content: "Click the 'New Client' button to add a client. You'll go through a multi-step form:",
        subsections: [
          {
            title: "Step 1: Basic Information",
            content: "Enter client name, phone, and date of birth. Email is optional. All fields marked with * are required.",
            type: "info",
            icon: "1️⃣"
          },
          {
            title: "Step 2: Medical History",
            content: "Record relevant medical history, injuries, surgeries, and current conditions.",
            type: "info",
            icon: "2️⃣"
          },
          {
            title: "Step 3: Goals & Notes",
            content: "Document client goals, preferences, and any additional notes for reference.",
            type: "info",
            icon: "3️⃣"
          }
        ]
      },
      {
        title: "Client Actions",
        content: "Each client card/row provides quick actions:",
        subsections: [
          {
            title: "View Client",
            content: "Click on the client name to view full details, assessment history, and treatment plans.",
            type: "info",
            icon: "👁️"
          },
          {
            title: "Edit Client",
            content: "Click the edit icon to update client information.",
            type: "info",
            icon: "✏️"
          },
          {
            title: "New Assessment",
            content: "Start a new SOP assessment for this client.",
            type: "info",
            icon: "📋"
          },
          {
            title: "Posture Analysis",
            content: "Launch posture analysis tool for this client.",
            type: "info",
            icon: "📸"
          }
        ]
      },
      {
        title: "Search & Filter",
        content: "Use the search bar to quickly find clients by name, email, or phone. Filter by status, last activity date, or other criteria.",
      },
      {
        title: "Client Selection",
        content: "Use the client selector in the header to set a selected client. This client context is maintained across different pages for seamless workflow.",
      }
    ]
  },

  clientDetail: {
    title: "Client Details Help",
    description: "View and manage comprehensive client information",
    sections: [
      {
        title: "Client Profile",
        content: "The client detail page provides a complete view of client information, assessment history, and treatment progress.",
      },
      {
        title: "Assessment History",
        content: "View all assessments performed for this client. Click on any assessment to view detailed results or continue incomplete assessments.",
        subsections: [
          {
            title: "Assessment Status",
            content: "Assessments are marked as Complete, In Progress, or Scheduled. Color coding helps you quickly identify status.",
            type: "info",
            icon: "🎨"
          },
          {
            title: "View Assessment",
            content: "Click the assessment date/name to view full results including test scores, analysis, and recommendations.",
            type: "info",
            icon: "📊"
          }
        ]
      },
      {
        title: "Posture Analysis Records",
        content: "Review all posture analyses performed for this client. View before/after comparisons and track progress over time.",
      },
      {
        title: "Treatment Plans",
        content: "Access active and past treatment plans. Assign drills from the drill library and track compliance.",
      },
      {
        title: "Notes & Communication",
        content: "Add session notes, communication logs, and track client progress. All notes are timestamped and searchable.",
      }
    ]
  },

  postureAnalysis: {
    title: "Posture Analysis Help",
    description: "AI-powered posture analysis using computer vision",
    sections: [
      {
        title: "Overview",
        content: "The Posture Analysis tool uses advanced AI to analyze client posture from photos. It identifies key body landmarks and provides detailed biomechanical analysis.",
      },
      {
        title: "🔬 Technical Architecture & Methodology",
        type: "technical",
        content: "Understanding the computational biomechanics and algorithmic decision-making behind the posture analysis system.",
        subsections: [
          {
            title: "Landmark Detection & Spatial Calibration",
            content: "The system employs MediaPipe's BlazePose neural network to detect 33 anatomical landmarks with sub-pixel accuracy. Spatial calibration utilizes client height as a known reference dimension to calculate the cm/pixel ratio, converting 2D pixel coordinates into real-world metric measurements. This calibration compensates for camera distance and lens distortion effects.",
            type: "info",
            icon: "🎯"
          },
          {
            title: "Multi-Planar Biomechanical Analysis",
            content: "Analysis is performed across four orthogonal views:\n\n**Anterior/Posterior (Coronal Plane):** Evaluates frontal plane asymmetries including head tilt, shoulder obliquity, pelvic obliquity, trunk lateral flexion, and pelvic shift. Measurements are reported as signed values (positive = right, negative = left).\n\n**Left/Right Lateral (Sagittal Plane):** Assesses sagittal plane alignment including craniovertebral angle (CVA), forward head posture, anterior/posterior pelvic tilt (ASIS-PSIS angle), hip-ankle horizontal offset, shoulder-ankle offset, and knee hyperextension/flexion angle.",
            type: "info",
            icon: "📐"
          },
          {
            title: "Classification Algorithm & Thresholds",
            content: "The classification system employs a hierarchical decision tree based on validated clinical thresholds:\n\n**Primary Decision Node:** Pelvic tilt angle ≥6° anterior → Classifies as Anterior Pelvic Tilt (APT). This threshold represents one standard deviation above normative values in asymptomatic populations.\n\n**Secondary Decision Node:** If APT negative, evaluate hip-ankle offset. Offset ≥2.5 cm anterior + knee hyperextension ≥5° → Classifies as Swayback posture.\n\n**Tertiary Assessment:** Trunk lean ≥1° → Lateralization classification (right vs left dominant patterns).",
            type: "info",
            icon: "🧮"
          },
          {
            title: "Severity Banding & Color Coding",
            content: "Measurements are mapped to severity bands using parametric thresholds:\n\n• **Level 0 (Green):** Within 1 SD of normative mean\n• **Level 1 (Yellow):** 1-2 SD from mean (minor deviation)\n• **Level 2 (Orange):** 2-3 SD from mean (moderate dysfunction)\n• **Level 3 (Red):** >3 SD from mean (severe dysfunction)\n\nSeverity bands are measurement-specific. For example, pelvic shift uses dynamic thresholds based on hip width percentage (3%, 6%, 10%) to account for individual anthropometric variation.",
            type: "info",
            icon: "🎨"
          },
          {
            title: "3D Model Construction & COM Estimation",
            content: "When lateral views are combined with anterior/posterior views, the system constructs a 3D point cloud model by triangulating landmark positions across views. Center of Mass (COM) is estimated using segmental mass distribution parameters (head: 8%, trunk: 50%, lower extremity segments: 42% total). COM displacement relative to base of support predicts fall risk and postural stability.",
            type: "info",
            icon: "📊"
          },
          {
            title: "Phase Classification Mapping",
            content: "Posture patterns map to Conor Harris Progression Model phases:\n\n**P1-APT:** Anterior pelvic tilt ≥6° indicates restriction in hip extension and/or excessive lumbar lordosis. Requires passive range restoration (Phase 1).\n\n**P1-SB:** Swayback pattern indicates poor hip extensor recruitment and anterior postural displacement. Also requires foundational Phase 1 intervention.\n\n**P2/P3:** Neutral alignment with minor asymmetries progresses to active control (P2) or strengthening (P3) based on active ROM assessment results.",
            type: "success",
            icon: "🎯"
          },
          {
            title: "Clinical Validation & Reliability",
            content: "The landmark detection algorithm demonstrates inter-rater reliability (ICC) of 0.92-0.97 for key angles compared to manual goniometry. Measurement precision is ±2° for angular measurements and ±0.3 cm for linear distances. The system is validated against clinical gold standards including radiographic analysis and 3D motion capture systems.",
            type: "success",
            icon: "✅"
          }
        ]
      },
      {
        title: "Taking Photos",
        content: "Capture photos in four standard views for comprehensive analysis:",
        subsections: [
          {
            title: "Anterior View (Front)",
            content: "Client faces camera directly, feet hip-width apart, arms relaxed at sides. Check for symmetry, shoulder level, pelvic alignment.",
            type: "info",
            icon: "⬆️"
          },
          {
            title: "Posterior View (Back)",
            content: "Client faces away from camera, same stance. Assess spinal alignment, scapular position, and hip symmetry.",
            type: "info",
            icon: "⬇️"
          },
          {
            title: "Left Lateral View",
            content: "Client's left side toward camera. Evaluate spinal curves, head position, and anterior/posterior tilt.",
            type: "info",
            icon: "⬅️"
          },
          {
            title: "Right Lateral View",
            content: "Client's right side toward camera. Compare with left side for asymmetries.",
            type: "info",
            icon: "➡️"
          },
          {
            title: "Upload or Paste",
            content: "Use the Upload button to select images from your device, or paste an image from the clipboard (Ctrl+V / Cmd+V). Pasted images appear in 'Assign Uploaded Images' for you to assign to the correct view.",
            type: "info",
            icon: "📤"
          }
        ]
      },
      {
        title: "Photo Requirements",
        content: "For optimal results:",
        subsections: [
          {
            title: "Lighting",
            content: "Use good, even lighting. Avoid harsh shadows. Natural daylight or soft indoor lighting works best.",
            type: "tip",
            icon: "💡"
          },
          {
            title: "Background",
            content: "Use a plain, uncluttered background. Contrast between client and background helps AI detection.",
            type: "tip",
            icon: "🖼️"
          },
          {
            title: "Clothing",
            content: "Client should wear form-fitting clothing. Shorts/sports bra for best landmark visibility.",
            type: "tip",
            icon: "👕"
          },
          {
            title: "Distance",
            content: CAPTURE_SETUP_HELP_DISTANCE_TEXT,
            type: "tip",
            icon: "📏"
          },
          {
            title: "Camera Height",
            content: CAPTURE_SETUP_HELP_HEIGHT_TEXT,
            type: "tip",
            icon: "📷"
          }
        ]
      },
      {
        title: "Analysis Results",
        content: "After processing, the analysis provides:",
        subsections: [
          {
            title: "Landmark Detection",
            content: "AI identifies 33 body landmarks including joints, spinal points, and key anatomical markers.",
            type: "info",
            icon: "🎯"
          },
          {
            title: "Angle Measurements",
            content: "Automatic calculation of key angles: forward head angle, shoulder alignment, pelvic tilt, knee alignment, etc.",
            type: "info",
            icon: "📐"
          },
          {
            title: "Asymmetry Analysis",
            content: "Comparison of left vs right side measurements. Highlights significant asymmetries requiring attention.",
            type: "info",
            icon: "⚖️"
          },
          {
            title: "Visual Overlays",
            content: "View skeleton overlay, angle indicators, and alignment grids on photos for easy visualization.",
            type: "info",
            icon: "📊"
          }
        ]
      },
      {
        title: "Interpreting Results",
        content: "The system provides color-coded results:",
        subsections: [
          {
            title: "Green Zone",
            content: "Measurements within normal/optimal range. No immediate concern.",
            type: "success",
            icon: "✅"
          },
          {
            title: "Yellow Zone",
            content: "Measurements slightly outside normal range. Monitor and consider corrective exercises.",
            type: "warning",
            icon: "⚠️"
          },
          {
            title: "Red Zone",
            content: "Measurements significantly outside normal range. Requires attention and intervention.",
            type: "warning",
            icon: "🔴"
          }
        ]
      },
      {
        title: "Saving & Reporting",
        content: "Save analysis results to client record. Generate PDF reports for client communication. Compare multiple analyses to track progress over time.",
      },
      {
        title: "Troubleshooting",
        content: "Common issues and solutions:",
        subsections: [
          {
            title: "AI Can't Detect Body",
            content: "Ensure full body is visible, good lighting, and plain background. Person should be clearly visible without obstructions.",
            type: "warning",
            icon: "⚠️"
          },
          {
            title: "Inaccurate Landmark Placement",
            content: "Poor lighting, baggy clothing, or extreme angles can affect detection. Retake photo following guidelines above.",
            type: "warning",
            icon: "⚠️"
          },
          {
            title: "Processing Taking Long",
            content: "Large image files take longer. Use camera/phone in standard resolution (not max). Check internet connection.",
            type: "info",
            icon: "⏱️"
          }
        ]
      }
    ]
  },

  sopAssessment: {
    title: "SOP Assessment Help",
    description: "Standard Operating Procedure assessment workflow",
    sections: [
      {
        title: "Overview",
        content: "The SOP Assessment is a comprehensive evaluation following the Biomechanics 2.0 protocol. It includes passive testing, active testing, and movement analysis to identify dysfunction and create treatment plans.",
      },
      {
        title: "🔬 Technical Architecture & Analysis Engine",
        type: "technical",
        content: "Understanding the computational assessment logic, normative databases, and automated treatment planning algorithms.",
        subsections: [
          {
            title: "Tripartite Assessment Framework",
            content: "The SOP follows a hierarchical testing sequence based on the Progression Model:\n\n**Passive Range of Motion (PROM):** Clinician-applied force to assess capsular/ligamentous restrictions and end-feel quality. Isolates joint integrity from neuromuscular factors. Tests performed to anatomical end-range with standardized positioning.\n\n**Active Range of Motion (AROM):** Client-generated movement assessing motor control, strength, and pain response within available PROM. Identifies neuromuscular limitations distinct from structural restrictions.\n\n**Functional Movement Patterns:** Multi-joint, task-specific movements (squat, hinge, lunge, push, pull, rotation) revealing compensation strategies and movement quality under load.",
            type: "info",
            icon: "🔬"
          },
          {
            title: "Normative Database & Comparative Analysis",
            content: "Each test parameter is compared against sex-specific, age-stratified normative values derived from peer-reviewed literature:\n\n**Hip Flexion:** 120° ± 10° (Roach & Miles, 1991)\n**Hip Internal Rotation:** 35° ± 5° (Ellison et al., 1990)\n**Ankle Dorsiflexion:** 10-15° with knee extended (Menz et al., 2005)\n**Thomas Test:** Thigh horizontal or below (Harvey, 1998)\n**Hip Extension (SoP):** 0° = full extension; negative = degrees short\n**Ober's Test:** Thigh to table without compensation (Reese & Bandy, 2010)\n\nThe system calculates deviation from normative mean in both absolute values and standard deviations to quantify dysfunction severity.",
            type: "info",
            icon: "📊"
          },
          {
            title: "Real-Time Severity Classification",
            content: "As measurements are entered, the system applies severity banding algorithms:\n\n**Green (Level 0):** Within 10% of normative mean - optimal function\n**Yellow (Level 1):** 10-25% deviation - minor limitation, monitor\n**Orange (Level 2):** 25-40% deviation - moderate dysfunction, intervention recommended\n**Red (Level 3):** >40% deviation or presence of pain - significant dysfunction, priority intervention\n\nColor feedback provides immediate clinical decision support during assessment.",
            type: "info",
            icon: "🎨"
          },
          {
            title: "Bilateral Asymmetry Detection",
            content: "The system performs automated left-right comparative analysis:\n\n**Asymmetry Index (AI):** AI = |Left - Right| / ((Left + Right)/2) × 100%\n\nAsymmetries >10% are flagged as clinically significant. Research demonstrates that bilateral differences >10-15% correlate with increased injury risk (Knapik et al., 1991). The system prioritizes asymmetric dysfunctions even when absolute values fall within normative ranges, as asymmetry often drives compensatory patterns.",
            type: "info",
            icon: "⚖️"
          },
          {
            title: "Dysfunction Hierarchy & Treatment Prioritization",
            content: "Post-assessment, the analysis engine ranks dysfunctions using a weighted scoring algorithm:\n\n**Weighting Factors:**\n• Severity level (Red = 3x, Orange = 2x, Yellow = 1x)\n• Bilateral asymmetry magnitude (>20% = 2x multiplier)\n• Position in kinetic chain (proximal dysfunctions weighted higher)\n• Pain presence (adds 1.5x multiplier)\n\nThe system generates a priority-ranked dysfunction list, focusing clinical attention on highest-impact limitations.",
            type: "info",
            icon: "🎯"
          },
          {
            title: "Automated Treatment Algorithm",
            content: "The system cross-references identified dysfunctions with the drill library's tagged exercises:\n\n**Step 1:** Match dysfunction tags (e.g., 'hip-flexion-limitation') to drill tags\n**Step 2:** Filter by appropriate phase (passive restriction → Phase 1 mobility drills)\n**Step 3:** Consider asymmetry (unilateral vs bilateral prescription)\n**Step 4:** Generate progression sequence following 7-phase model\n\nRecommendations include sets/reps/frequency based on dysfunction severity and phase-appropriate dosing parameters.",
            type: "success",
            icon: "🧠"
          },
          {
            title: "Qualitative Data Integration",
            content: "Beyond quantitative measurements, the notes system captures critical qualitative variables:\n\n**End-Feel Classification:** Capsular, bony, soft tissue approximation, muscle spasm, empty (pain-limited)\n**Pain Provocation:** Location, intensity (0-10 VAS), arc of pain within range\n**Compensation Patterns:** Joint substitution, muscle guarding, breath-holding\n\nThis qualitative data, while not algorithmically processed, provides essential clinical context that overrides purely numerical decision-making. The system prompts for notes when measurements fall outside normal ranges to ensure comprehensive documentation.",
            type: "info",
            icon: "📝"
          },
          {
            title: "Measurement Precision & Validity",
            content: "Goniometric measurements (via Visio Meter 2.0) achieve ±2-3° precision, consistent with manual goniometry standards (Norkin & White, 2009). The digital system eliminates parallax error and provides permanent visual documentation for progress tracking. Store images selectively for baseline, significant findings, or disputed measurements to balance documentation needs with database efficiency.",
            type: "success",
            icon: "✅"
          }
        ]
      },
      {
        title: "Assessment Sections",
        content: "The assessment is divided into organized sections:",
        subsections: [
          {
            title: "Passive Testing",
            content: "Evaluate passive range of motion at key joints. Use the Visio Meter 2.0 for precise angle measurements.",
            type: "info",
            icon: "📏"
          },
          {
            title: "Active Testing",
            content: "Assess active movement patterns and strength. Document compensation patterns and pain responses.",
            type: "info",
            icon: "💪"
          },
          {
            title: "Movement Screen",
            content: "Evaluate functional movement patterns including squat, hinge, lunge, push, pull, rotation, and gait.",
            type: "info",
            icon: "🏃"
          },
          {
            title: "Special Tests",
            content: "Perform specific orthopedic tests based on client presentation and initial findings.",
            type: "info",
            icon: "🔬"
          }
        ]
      },
      {
        title: "Using the Assessment Form",
        content: "Input values and observations for each test:",
        subsections: [
          {
            title: "Measurement Input",
            content: "Enter values manually or use the Visio Meter 2.0 button (camera icon) for precise goniometric measurements. Hip Extension uses free numeric input; Grounding Performance defaults to dynamometer mode with an optional perceived (1-10) toggle; foot presentation remains dropdown-based.",
            type: "info",
            icon: "📝"
          },
          {
            title: "Color Coding",
            content: "Input fields change color based on values: Green (normal), Yellow (borderline), Orange (concerning), Red (significant dysfunction). This provides instant visual feedback on test results.",
            type: "info",
            icon: "🎨"
          },
          {
            title: "Left vs Right",
            content: "Most tests have separate inputs for left (L) and right (R) sides. Compare values to identify asymmetries. Pay special attention to differences greater than 10%.",
            type: "info",
            icon: "⚖️"
          },
          {
            title: "Per-Measurement Notes",
            content: "Each measurement now has its own dedicated notes button (file icon) in the upper-left corner. Click this button to add measurement-specific notes, observations, pain responses, end-feel quality, or compensation patterns for that specific test and side.",
            type: "info",
            icon: "📝"
          },
          {
            title: "Notes with Images",
            content: "If you've stored an image with a measurement using Visio Meter 2.0, the notes button will display a small red play indicator. When you open notes for measurements with stored images, you'll see both the image and a text area for notes directly below it.",
            type: "info",
            icon: "🖼️"
          },
          {
            title: "Notes without Images",
            content: "You can add notes to any measurement even without storing an image. Simply click the notes button and type your observations. This is useful for documenting qualitative findings like pain location, muscle guarding, or movement quality.",
            type: "info",
            icon: "📋"
          }
        ]
      },
      {
        title: "Visio Meter 2.0 Integration",
        content: "Click the camera icon on any measurement field to open the Visio Meter 2.0 tool. This provides precise goniometric measurement using your camera or uploaded photos/videos. See 'Visio Meter 2.0 Help' for detailed instructions.",
      },
      {
        title: "Saving & Progress",
        content: "Assessment progress is auto-saved as you work. You can exit and return to complete later. All entered data is preserved.",
        subsections: [
          {
            title: "Save Draft",
            content: "Click 'Save' to save progress without finalizing. Assessment status remains 'In Progress'.",
            type: "info",
            icon: "💾"
          },
          {
            title: "Complete Assessment",
            content: "Click 'Complete' when all sections are finished. Status changes to 'Complete' and analysis is generated.",
            type: "success",
            icon: "✅"
          }
        ]
      },
      {
        title: "Analysis & Recommendations",
        content: "After completion, the system analyzes results and provides:",
        subsections: [
          {
            title: "Dysfunction Identification",
            content: "Highlights key dysfunctions based on test results and established norms.",
            type: "info",
            icon: "🎯"
          },
          {
            title: "Priority Ranking",
            content: "Ranks dysfunctions by severity and impact on function.",
            type: "info",
            icon: "📊"
          },
          {
            title: "Exercise Recommendations",
            content: "Suggests appropriate drills from the drill library based on identified dysfunctions.",
            type: "info",
            icon: "🏋️"
          },
          {
            title: "Treatment Plan",
            content: "Generates a structured treatment plan with progression guidelines.",
            type: "info",
            icon: "📋"
          }
        ]
      },
      {
        title: "Best Practices",
        content: "Tips for effective assessments:",
        subsections: [
          {
            title: "Follow Protocol Order",
            content: "Complete sections in order: passive tests first, then active, then functional movement. This provides logical progression and prevents fatigue affecting results.",
            type: "tip",
            icon: "🔢"
          },
          {
            title: "Document with Notes",
            content: "Use the per-measurement notes feature liberally. Document pain location (e.g., 'Sharp pain at 85°'), end-feel quality (e.g., 'Firm capsular', 'Soft tissue approximation'), compensation patterns (e.g., 'Hip hiking during movement'), and any unusual findings. These qualitative observations are often more valuable than the numeric measurements.",
            type: "tip",
            icon: "📝"
          },
          {
            title: "Note Compensation",
            content: "Document any compensation patterns observed even if test result appears 'normal'. Compensation is often more important than the measurement.",
            type: "tip",
            icon: "👁️"
          },
          {
            title: "Compare Sides",
            content: "Always compare left vs right. Asymmetries often indicate dysfunction even if both sides are within 'normal' range. Use notes to document how the movement quality differs between sides.",
            type: "tip",
            icon: "⚖️"
          },
          {
            title: "Image Storage Strategy",
            content: "Store images selectively for baseline measurements, significant findings, or progress tracking. Not every measurement needs an image. Use notes alone for routine findings to save database space.",
            type: "tip",
            icon: "💾"
          },
          {
            title: "Client Comfort",
            content: "Ensure client is comfortable and relaxed. Tension or guarding can affect measurements significantly. Note any guarding or apprehension in the measurement notes.",
            type: "tip",
            icon: "😌"
          }
        ]
      }
    ]
  },

  drillLibrary: {
    title: "Drill Library Help",
    description: "Comprehensive exercise and drill management system",
    sections: [
      {
        title: "Overview",
        content: "The Drill Library is your comprehensive database of corrective exercises, drills, and movement patterns. Use it to build treatment plans, create progressions, and educate clients.",
      },
      {
        title: "Library Organization",
        content: "Drills are organized using multiple classification systems:",
        subsections: [
          {
            title: "Categories",
            content: "Main categories like Mobility, Stability, Strength, Power, Conditioning. Filter by category for quick access.",
            type: "info",
            icon: "📁"
          },
          {
            title: "Body Regions",
            content: "Filter by body region: Upper Body, Lower Body, Core, Spine, etc.",
            type: "info",
            icon: "🎯"
          },
          {
            title: "Movement Patterns",
            content: "Organized by fundamental movement patterns: Squat, Hinge, Push, Pull, Rotation, Gait.",
            type: "info",
            icon: "🏃"
          },
          {
            title: "Difficulty Levels",
            content: "Drills rated by difficulty: Beginner, Intermediate, Advanced, Expert. Build appropriate progressions.",
            type: "info",
            icon: "📊"
          },
          {
            title: "Tags",
            content: "Custom tags for specific dysfunctions, conditions, or equipment needs. Create your own tags for personalized organization.",
            type: "info",
            icon: "🏷️"
          }
        ]
      },
      {
        title: "Search & Filter",
        content: "Powerful search and filter tools help you find the right drill:",
        subsections: [
          {
            title: "Search Bar",
            content: "Type drill name, description, or keywords. Search is instant and searches across all fields.",
            type: "info",
            icon: "🔍"
          },
          {
            title: "Multiple Filters",
            content: "Apply multiple filters simultaneously. Filters work together to narrow results (AND logic).",
            type: "info",
            icon: "🎚️"
          },
          {
            title: "Tag Cloud",
            content: "Visual tag cloud shows popular tags. Click any tag to filter by that tag instantly.",
            type: "info",
            icon: "☁️"
          },
          {
            title: "Quick Filters",
            content: "Preset filter combinations for common needs: Hip Mobility, Shoulder Stability, Core Strength, etc.",
            type: "info",
            icon: "⚡"
          }
        ]
      },
      {
        title: "Viewing Drill Details",
        content: "Click any drill card to view complete information:",
        subsections: [
          {
            title: "Video Demonstration",
            content: "Watch embedded video showing proper execution. Videos include setup, execution, common mistakes, and coaching cues.",
            type: "info",
            icon: "🎥"
          },
          {
            title: "Step-by-Step Instructions",
            content: "Written instructions broken down into setup, execution steps, and key points.",
            type: "info",
            icon: "📝"
          },
          {
            title: "Coaching Cues",
            content: "Important verbal cues to use when coaching this drill. Help clients understand what they should feel.",
            type: "info",
            icon: "💬"
          },
          {
            title: "Common Mistakes",
            content: "What to watch for and correct. Helps you spot and fix execution errors.",
            type: "info",
            icon: "⚠️"
          },
          {
            title: "Progressions & Regressions",
            content: "Links to harder (progression) and easier (regression) versions of the drill.",
            type: "info",
            icon: "↕️"
          },
          {
            title: "Related Drills",
            content: "Similar drills targeting the same dysfunction or movement pattern.",
            type: "info",
            icon: "🔗"
          }
        ]
      },
      {
        title: "Adding & Editing Drills",
        content: "Customize your library with your own drills:",
        subsections: [
          {
            title: "New Drill",
            content: "Click 'New Drill' to add a custom drill. Fill in all relevant fields including name, description, instructions, video link, categories, and tags.",
            type: "info",
            icon: "➕"
          },
          {
            title: "Edit Drill",
            content: "Click edit icon on any drill to modify details. Changes are saved automatically.",
            type: "info",
            icon: "✏️"
          },
          {
            title: "Video Links",
            content: "Paste YouTube, Vimeo, or direct video URLs. The system automatically embeds videos for playback.",
            type: "info",
            icon: "🎬"
          },
          {
            title: "Bulk Edit",
            content: "Select multiple drills and edit tags, categories, or other fields in bulk. Saves time when organizing large libraries.",
            type: "info",
            icon: "📦"
          }
        ]
      },
      {
        title: "Building Treatment Plans",
        content: "Use drills to create client treatment plans:",
        subsections: [
          {
            title: "Add to Plan",
            content: "Click 'Add to Plan' on any drill card. Choose sets, reps, duration, and frequency.",
            type: "info",
            icon: "➕"
          },
          {
            title: "Progression Planning",
            content: "Build multi-week progressions by scheduling drill progressions over time.",
            type: "info",
            icon: "📈"
          },
          {
            title: "Client Assignment",
            content: "Assign plans to clients. They can access drills through their client portal (if enabled).",
            type: "info",
            icon: "👤"
          },
          {
            title: "Print/Export",
            content: "Generate PDF workout sheets with drill images and instructions for clients.",
            type: "info",
            icon: "🖨️"
          }
        ]
      },
      {
        title: "Import & Export",
        content: "Manage your drill database:",
        subsections: [
          {
            title: "Import Drills",
            content: "Import drills from CSV file. Template available for download. Useful for bulk adding drills.",
            type: "info",
            icon: "📥"
          },
          {
            title: "Export Library",
            content: "Export your entire drill library or filtered selection to CSV. Back up your data or share with colleagues.",
            type: "info",
            icon: "📤"
          },
          {
            title: "Library Sharing",
            content: "Share curated drill collections with other practitioners (requires permission settings).",
            type: "info",
            icon: "🤝"
          }
        ]
      }
    ]
  },

  progressionModel: {
    title: "Progression Model Help",
    description: "Biomechanics 2.0 Progression Framework",
    sections: [
      {
        title: "Overview",
        content: "The Progression Model is the foundational framework for organizing assessments, interventions, and training progressions. It provides a systematic approach to moving clients from dysfunction to optimal performance.",
      },
      {
        title: "The 3-Phase Biomechanics 2.0 Model",
        content: "Harry's clinical progression model has three phases tied to SoP assessment gates:",
        subsections: [
          {
            title: "Phase 1: Restore expansion & bilateral mobility",
            content: "Erase compensations and rebuild foundational bilateral ROM (Shoulder IR, SLR, Hip Extension). APT clients restore posterior expansion; Swayback clients restore anterior expansion. Pass the Walking Test before advancing.",
            type: "info",
            icon: "1️⃣"
          },
          {
            title: "Phase 2: Train the non-lateralized side",
            content: "Address asymmetry and improve occupancy on the non-lateralized side with unilateral drills. Clear bilateral Phase 2 gates, then pass the Occupancy Test on the originally lateralized side.",
            type: "info",
            icon: "2️⃣"
          },
          {
            title: "Phase 3: Integrate on the lateralized side",
            content: "Integrate restored mechanics into loaded, dynamic, and sport-specific movement. Maintain Phase 2 gains while building durability and grounding on the originally lateralized side.",
            type: "info",
            icon: "3️⃣"
          }
        ]
      },
      {
        title: "Applying the Model",
        content: "How to use the progression model in practice:",
        subsections: [
          {
            title: "Assessment-Based Entry",
            content: "Use SOP assessment results to identify which phase the client needs. Don't skip phases - foundation is critical.",
            type: "tip",
            icon: "🎯"
          },
          {
            title: "Phase Prerequisites",
            content: "Client must demonstrate competency in current phase before progressing. Regression is not failure - it's finding the right starting point.",
            type: "tip",
            icon: "✅"
          },
          {
            title: "Non-Linear Progression",
            content: "Different body regions may be in different phases simultaneously. Hip might be in Phase 3 while shoulder is still Phase 1.",
            type: "info",
            icon: "🔄"
          },
          {
            title: "Return to Foundation",
            content: "After injury or setbacks, return to appropriate phase. Don't assume prior level is maintained.",
            type: "warning",
            icon: "⚠️"
          }
        ]
      },
      {
        title: "Visual Framework",
        content: "The progression model page includes an inline diagram, phase cards with assessment gate tables synced to SoP rules, glossary tooltips, and a troubleshooting decision tree. When a client is selected, their current phase and gate status appear at the top.",
      },
      {
        title: "Integration with Assessments",
        content: "SoP assessment results determine phase (P1/P2/P3). Assessment gates on the page match evaluation-rules.yaml — cervical tests are optional and do not block progression.",
      },
      {
        title: "Treatment Planning",
        content: "Use the progression model to guide treatment plan creation. The drill library is organized to align with progression phases, making it easy to select appropriate interventions.",
      }
    ]
  },

  coaching: {
    title: "Coaching Resources Help",
    description: "Educational resources and coaching guidelines",
    sections: [
      {
        title: "Overview",
        content: "The Coaching section provides educational resources, teaching guides, and best practices for implementing the Biomechanics 2.0 methodology.",
      },
      {
        title: "Resource Types",
        content: "Access various coaching resources:",
        subsections: [
          {
            title: "Educational Videos",
            content: "Video lectures on biomechanics principles, assessment techniques, and intervention strategies.",
            type: "info",
            icon: "🎥"
          },
          {
            title: "Coaching Guides",
            content: "Step-by-step guides for teaching specific drills and concepts to clients.",
            type: "info",
            icon: "📚"
          },
          {
            title: "Case Studies",
            content: "Real-world examples of assessment, diagnosis, and treatment progressions.",
            type: "info",
            icon: "📋"
          },
          {
            title: "Research Papers",
            content: "Supporting research and evidence base for the methodology.",
            type: "info",
            icon: "🔬"
          }
        ]
      },
      {
        title: "Coaching Principles",
        content: "Core principles for effective coaching:",
        subsections: [
          {
            title: "Assessment First",
            content: "Always assess before prescribing. Guessing leads to ineffective programs.",
            type: "tip",
            icon: "🎯"
          },
          {
            title: "Individual Variability",
            content: "Every client is unique. Don't apply cookie-cutter solutions.",
            type: "tip",
            icon: "👤"
          },
          {
            title: "Progressive Overload",
            content: "Challenge appropriately. Too easy = no adaptation. Too hard = injury risk or compensation.",
            type: "tip",
            icon: "📈"
          },
          {
            title: "Movement Quality Over Quantity",
            content: "Perfect practice makes perfect. Poor-quality reps reinforce dysfunction.",
            type: "tip",
            icon: "✨"
          },
          {
            title: "Education & Ownership",
            content: "Educate clients on the 'why'. Understanding increases buy-in and compliance.",
            type: "tip",
            icon: "🧠"
          }
        ]
      },
      {
        title: "Client Communication",
        content: "Best practices for effective client communication and education using the platform's resources.",
      }
    ]
  },

  askHarry: {
    title: "Ask Harry Help",
    description: "AI-powered biomechanics assistant",
    sections: [
      {
        title: "Overview",
        content: "Ask Harry is your AI assistant trained on the Biomechanics 2.0 methodology. Use it to get quick answers to biomechanics questions, assessment guidance, and treatment recommendations.",
      },
      {
        title: "How to Use",
        content: "Type your question in natural language and Harry will provide detailed, methodology-specific answers.",
        subsections: [
          {
            title: "Question Types",
            content: "Harry can help with: Assessment interpretation, Drill selection, Progression planning, Anatomy questions, Client education, Methodology clarification.",
            type: "info",
            icon: "❓"
          },
          {
            title: "Context Awareness",
            content: "Harry has access to your current client context (if selected) and can provide personalized recommendations based on assessment data.",
            type: "info",
            icon: "🎯"
          },
          {
            title: "Follow-Up Questions",
            content: "Ask follow-up questions to dive deeper. Harry maintains conversation context for multi-turn interactions.",
            type: "info",
            icon: "💬"
          }
        ]
      },
      {
        title: "Example Questions",
        content: "Try asking Harry things like:",
        subsections: [
          {
            title: "Assessment Help",
            content: "What does limited hip internal rotation indicate? How should I interpret pain during passive testing?",
            type: "info",
            icon: "📋"
          },
          {
            title: "Drill Selection",
            content: "What drills address limited ankle dorsiflexion? Show me progressions for hip hinge patterns.",
            type: "info",
            icon: "🏋️"
          },
          {
            title: "Treatment Planning",
            content: "How should I sequence corrective exercises? What's the optimal frequency for mobility work?",
            type: "info",
            icon: "📅"
          },
          {
            title: "Methodology Questions",
            content: "Explain the relationship between passive and active range. When should I use isometric vs dynamic exercises?",
            type: "info",
            icon: "📚"
          }
        ]
      },
      {
        title: "Tips for Best Results",
        content: "Get the most from Harry:",
        subsections: [
          {
            title: "Be Specific",
            content: "Include relevant details: Which joint? What's the limitation? What have you tried?",
            type: "tip",
            icon: "🎯"
          },
          {
            title: "Provide Context",
            content: "Mention client goals, sport/activity, injury history when relevant.",
            type: "tip",
            icon: "📝"
          },
          {
            title: "Ask Follow-Ups",
            content: "Don't hesitate to ask for clarification or more detail.",
            type: "tip",
            icon: "🔄"
          }
        ]
      },
      {
        title: "Limitations",
        content: "Harry is a tool to support your clinical decision-making, not replace it. Always use your professional judgment and consider individual client factors.",
        subsections: [
          {
            title: "Not Medical Advice",
            content: "Harry provides educational information based on methodology. It's not a substitute for medical diagnosis or treatment.",
            type: "warning",
            icon: "⚠️"
          }
        ]
      }
    ]
  },

  settings: {
    title: "Settings Help",
    description: "Customize your application preferences",
    sections: [
      {
        title: "Profile Settings",
        content: "Update your personal information, credentials, and professional details.",
      },
      {
        title: "Notification Preferences",
        content: "Control which notifications you receive and how you receive them (email, in-app, push).",
      },
      {
        title: "Display Preferences",
        content: "Customize the interface:",
        subsections: [
          {
            title: "Theme",
            content: "Choose between light, dark, or auto (follows system preference) themes.",
            type: "info",
            icon: "🎨"
          },
          {
            title: "Units",
            content: "Select measurement units: Metric (cm, kg) or Imperial (inches, lbs).",
            type: "info",
            icon: "📏"
          },
          {
            title: "Date Format",
            content: "Choose your preferred date format.",
            type: "info",
            icon: "📅"
          }
        ]
      },
      {
        title: "Data Management",
        content: "Export your data, clear cache, or request data deletion.",
      },
      {
        title: "Privacy & Security",
        content: "Manage data privacy settings, two-factor authentication, and session security.",
      }
    ]
  },

  adminUsers: {
    title: "User Management Help",
    description: "Manage users, assign roles, and configure permissions",
    sections: [
      {
        title: "Overview",
        content: "The User Management page allows administrators to view all users, assign roles, edit role assignments, and configure permissions for the application.",
      },
      {
        title: "Assigning Roles",
        content: "To assign a role to a new or existing user:",
        subsections: [
          {
            title: "Step 1: Enter Email",
            content: "Enter the user's email address in the 'Assign Role to User' section at the top of the page.",
            type: "info",
            icon: "1️⃣"
          },
          {
            title: "Step 2: Select Role",
            content: "Choose the role to assign from the dropdown menu. Available roles include Administrator, Practitioner, Tutor, and Client.",
            type: "info",
            icon: "2️⃣"
          },
          {
            title: "Step 3: Assign",
            content: "Click 'Assign Role' to create the role assignment. The user will appear in the users table below.",
            type: "info",
            icon: "3️⃣"
          }
        ]
      },
      {
        title: "Managing Existing Users",
        content: "In the users table, you can:",
        subsections: [
          {
            title: "Edit Role",
            content: "Click the edit (pencil) icon to change a user's role. Select the new role from the dialog and click 'Update Role'.",
            type: "info",
            icon: "✏️"
          },
          {
            title: "Remove Role",
            content: "Click the delete (trash) icon to remove a user's role assignment. This will revert them to the default practitioner role.",
            type: "warning",
            icon: "🗑️"
          },
          {
            title: "Filter & Search",
            content: "Use the filter controls to search by email or user ID, filter by role, or filter by status (Active vs Pre-Assigned).",
            type: "tip",
            icon: "🔍"
          }
        ]
      },
      {
        title: "User Status",
        content: "Users can have two statuses:",
        subsections: [
          {
            title: "Active",
            content: "Users who have logged in at least once. Their role assignment is linked to their Firebase Auth UID.",
            type: "success",
            icon: "✅"
          },
          {
            title: "Pre-Assigned",
            content: "Users who have been assigned a role but haven't logged in yet. Their role assignment uses a placeholder ID and will be migrated to their actual UID when they first log in.",
            type: "info",
            icon: "⏳"
          }
        ]
      },
      {
        title: "Permissions Tab",
        content: "The Permissions tab allows you to configure fine-grained permissions for each role, controlling what actions users can perform in the application.",
      }
    ]
  },

  // Modal-specific help content
  visioMeter: {
    title: "Visio Meter 2.0 Help",
    description: "Digital goniometer for precise angle measurements",
    sections: [
      {
        title: "What is Visio Meter 2.0?",
        content: "Visio Meter 2.0 is a digital goniometer tool that uses your camera or uploaded images/videos to measure joint angles precisely. It replaces traditional manual goniometers with a more accurate, documented, and efficient digital solution.",
      },
      {
        title: "Getting Started",
        content: "Follow these steps to take a measurement:",
        subsections: [
          {
            title: "Step 1: Select Test & Side",
            content: "Choose which passive test you're measuring and which side (Left or Right). The tool will remember the current test context.",
            type: "info",
            icon: "1️⃣"
          },
          {
            title: "Step 2: Capture Image",
            content: "Use your camera to capture the client at end range of motion, or upload an existing photo/video. For video, you can scrub to the exact frame you want.",
            type: "info",
            icon: "2️⃣"
          },
          {
            title: "Step 3: Position Goniometer",
            content: "Drag the three control points (pivot and two arms) to align with the joint and body segments.",
            type: "info",
            icon: "3️⃣"
          },
          {
            title: "Step 4: Confirm & Save",
            content: "Review the angle measurement and click 'Input' to save it to your assessment. Optionally toggle 'Store Image' to save the measurement image.",
            type: "info",
            icon: "4️⃣"
          }
        ]
      },
      {
        title: "Media Sources",
        content: "Visio Meter supports multiple media sources:",
        subsections: [
          {
            title: "Live Camera",
            content: "Use your device camera for real-time capture. Click 'Capture' to freeze a frame for measurement. Great for in-person assessments.",
            type: "info",
            icon: "📷"
          },
          {
            title: "Upload Image",
            content: "Upload a photo from your device. Works with JPG, PNG, and other image formats. Useful for pre-captured images. You can also paste an image from the clipboard (Ctrl+V / Cmd+V) when the tool is open.",
            type: "info",
            icon: "🖼️"
          },
          {
            title: "Upload Video",
            content: "Upload a video file. Use playback controls to find the exact frame showing end range. Perfect for movement analysis.",
            type: "info",
            icon: "🎥"
          }
        ]
      },
      {
        title: "Controls Explained",
        content: "Understanding the Visio Meter controls:",
        subsections: [
          {
            title: "📤 Upload Button",
            content: "Click to upload an image or video file from your device. Accepts most common formats.",
            type: "info",
            icon: "📤"
          },
          {
            title: "📷 Capture Button",
            content: "When using camera: Freezes the current frame so you can measure it. After capturing: Changes to 'Retake' - click to unfreeze and capture a new frame.",
            type: "info",
            icon: "📷"
          },
          {
            title: "💾 Store Image Toggle",
            content: "When ON: Saves a snapshot of your measurement (image + goniometer overlay). When OFF: Only the angle value is saved. Best Practice: Only store images when you need visual documentation to save database space.",
            type: "info",
            icon: "💾"
          },
          {
            title: "✅ Input Button",
            content: "Confirms the measurement and saves it to the assessment form. The modal will close and return you to the assessment page.",
            type: "info",
            icon: "✅"
          },
          {
            title: "🔄 Switch Camera Button",
            content: "If you have multiple cameras (front/back on mobile), this button switches between them.",
            type: "info",
            icon: "🔄"
          }
        ]
      },
      {
        title: "Using the Digital Goniometer",
        content: "The goniometer has three control points:",
        subsections: [
          {
            title: "Center Pivot (Blue Circle)",
            content: "Place this at the joint center (axis of rotation). This is the vertex of the angle being measured.",
            type: "info",
            icon: "🔵"
          },
          {
            title: "Arm 1 (Red Circle)",
            content: "Align with the proximal body segment (closer to body center). This forms one side of the angle.",
            type: "info",
            icon: "🔴"
          },
          {
            title: "Arm 2 (Green Circle)",
            content: "Align with the distal body segment (away from body center). This forms the other side of the angle.",
            type: "info",
            icon: "🟢"
          }
        ]
      },
      {
        title: "Positioning Technique",
        content: "How to accurately position the goniometer:",
        subsections: [
          {
            title: "Step 1",
            content: "Click and drag the center pivot (blue) to the joint center - this is the axis of rotation for the movement.",
            type: "info",
            icon: "1️⃣"
          },
          {
            title: "Step 2",
            content: "Drag arm 1 (red) to align with the fixed/reference body segment. This is typically the more proximal segment.",
            type: "info",
            icon: "2️⃣"
          },
          {
            title: "Step 3",
            content: "Drag arm 2 (green) to align with the moving body segment. This is typically the more distal segment.",
            type: "info",
            icon: "3️⃣"
          },
          {
            title: "Step 4",
            content: "The angle updates automatically as you move the arms. Fine-tune positioning for accuracy.",
            type: "info",
            icon: "4️⃣"
          }
        ]
      },
      {
        title: "Video Playback Controls",
        content: "When working with video files, you have precise frame control:",
        subsections: [
          {
            title: "▶️ Play/Pause",
            content: "Control video playback to find the right moment.",
            type: "info",
            icon: "▶️"
          },
          {
            title: "⏮️ ⏭️ Frame Step",
            content: "Move forward/backward by single frames for precise frame selection.",
            type: "info",
            icon: "⏮️"
          },
          {
            title: "🎚️ Timeline Slider",
            content: "Scrub through the video quickly to find the approximate frame.",
            type: "info",
            icon: "🎚️"
          },
          {
            title: "⚡ Speed Controls",
            content: "Adjust playback speed (0.25x - 2x) to slow down fast movements.",
            type: "info",
            icon: "⚡"
          }
        ]
      },
      {
        title: "Tips & Best Practices",
        content: "Get the most accurate measurements:",
        subsections: [
          {
            title: "Lighting",
            content: "Ensure good lighting so body landmarks are clearly visible. Avoid harsh shadows.",
            type: "tip",
            icon: "💡"
          },
          {
            title: "Camera Position",
            content: "Position camera perpendicular to the plane of movement. Side views for sagittal plane, front/back views for frontal/transverse plane.",
            type: "tip",
            icon: "📐"
          },
          {
            title: "Full Body View",
            content: "Capture the entire limb/joint being measured, not just the joint itself. You need to see both body segments clearly.",
            type: "tip",
            icon: "👤"
          },
          {
            title: "End Range",
            content: "Capture at the end range of motion for accurate assessment. This is where limitation shows.",
            type: "tip",
            icon: "📏"
          },
          {
            title: "Store Selectively",
            content: "Only store images when you need documentation (baseline, progress photos, unusual findings). This saves database space.",
            type: "tip",
            icon: "💾"
          },
          {
            title: "Zoom In",
            content: "Use browser zoom (Ctrl/Cmd +) for precise goniometer placement on the exact joint center.",
            type: "tip",
            icon: "🔍"
          }
        ]
      },
      {
        title: "Viewing Stored Images & Notes",
        content: "When you store an image with a measurement:",
        subsections: [
          {
            title: "Notes Button with Image Indicator",
            content: "When an image is stored for a measurement, the notes button (file icon) in the upper-left corner displays a small red play indicator. This shows at a glance which measurements have both images and notes.",
            type: "info",
            icon: "🔴"
          },
          {
            title: "Opening Notes",
            content: "Click the notes button to open the notes dialog. If an image is stored, you'll see the measurement image with goniometer overlay at the top, followed by an editable notes text area below.",
            type: "info",
            icon: "👁️"
          },
          {
            title: "Adding/Editing Notes",
            content: "Type or edit notes in the text area below the image. Notes are automatically saved when you close the dialog. You can add contextual information about the measurement, pain responses, end-feel, or observations.",
            type: "info",
            icon: "✏️"
          },
          {
            title: "Deleting Images",
            content: "If you need to remove a stored image, click the 'Delete Image' button in the notes dialog. This removes the image but preserves the angle measurement and any notes you've written.",
            type: "info",
            icon: "🗑️"
          }
        ]
      },
      {
        title: "Troubleshooting",
        content: "Common issues and solutions:",
        subsections: [
          {
            title: "Camera Not Working",
            content: "Check browser permissions. Click the camera icon in your browser's address bar and allow camera access. Try refreshing the page.",
            type: "warning",
            icon: "⚠️"
          },
          {
            title: "Can't See Goniometer Controls",
            content: "Make sure you've selected a test and side, then captured/frozen a frame first. Goniometer only appears after you have a still image.",
            type: "warning",
            icon: "⚠️"
          },
          {
            title: "Angle Measurement Seems Incorrect",
            content: "Verify that the center pivot is exactly at the joint center and both arms are properly aligned with the body segments. Small positioning errors compound.",
            type: "warning",
            icon: "⚠️"
          },
          {
            title: "Video Won't Upload",
            content: "Check file size (max 100MB) and format (MP4, MOV, AVI supported). Large files may take time to process. Check your internet connection.",
            type: "warning",
            icon: "⚠️"
          },
          {
            title: "Image Quality Poor",
            content: "Use higher resolution camera settings if available. Ensure good lighting. Clean camera lens. Hold device steady to avoid motion blur.",
            type: "warning",
            icon: "⚠️"
          }
        ]
      }
    ]
  },

  clientForm: {
    title: "Client Form Help",
    description: "Adding and editing client information",
    sections: [
      {
        title: "Multi-Step Form",
        content: "The client form uses a multi-step design to organize information logically and reduce overwhelm. Progress through each step to complete the client profile.",
      },
      {
        title: "Required vs Optional Fields",
        content: "Fields marked with an asterisk (*) are required to save the client. Optional fields enhance the client record but can be added later.",
        subsections: [
          {
            title: "Basic Information (Step 1)",
            content: "Name is required. Email and phone are highly recommended for communication. Date of birth helps with age-related assessment norms.",
            type: "info",
            icon: "📝"
          },
          {
            title: "Medical History (Step 2)",
            content: "Document injuries, surgeries, chronic conditions, and medications. This context is critical for safe assessment and programming.",
            type: "info",
            icon: "🏥"
          },
          {
            title: "Goals & Notes (Step 3)",
            content: "Record client goals, preferences, and relevant context. This guides treatment planning and helps you understand what success means to them.",
            type: "info",
            icon: "🎯"
          }
        ]
      },
      {
        title: "Saving & Progress",
        content: "Click 'Save' to save the client and return to the client list. Click 'Cancel' to discard changes and return without saving.",
      }
    ]
  },

  drillForm: {
    title: "Drill Form Help",
    description: "Adding and editing drills in your library",
    sections: [
      {
        title: "Drill Information",
        content: "Provide complete information for effective drill use:",
        subsections: [
          {
            title: "Name & Description",
            content: "Use clear, descriptive names. Include key details in description: equipment needed, space requirements, primary benefits.",
            type: "info",
            icon: "📝"
          },
          {
            title: "Instructions",
            content: "Write step-by-step setup and execution instructions. Include sets, reps, tempo guidelines.",
            type: "info",
            icon: "📋"
          },
          {
            title: "Video Link",
            content: "Paste YouTube or Vimeo URL. Video demonstrations significantly improve client understanding and compliance.",
            type: "info",
            icon: "🎥"
          },
          {
            title: "Categories & Tags",
            content: "Select relevant categories and add tags. Good organization makes drills easy to find later.",
            type: "info",
            icon: "🏷️"
          },
          {
            title: "Difficulty Level",
            content: "Rate difficulty honestly. This helps with progression planning and prevents inappropriate exercise selection.",
            type: "info",
            icon: "📊"
          }
        ]
      }
    ]
  },

  selectClient: {
    title: "Client Selection Help",
    description: "Setting the selected client context",
    sections: [
      {
        title: "Why Select a Client?",
        content: "Selecting a client sets the active context for assessments, posture analysis, and treatment planning. The selected client persists as you navigate between pages.",
      },
      {
        title: "How to Select",
        content: "Use the 'Select Client' dropdown in the header. Type to search for clients by name. Click a client to set them as active. The current client shows in the header.",
      },
      {
        title: "Context-Dependent Features",
        content: "Some features require a selected client:",
        subsections: [
          {
            title: "SOP Assessment",
            content: "You must select a client before starting a new assessment.",
            type: "info",
            icon: "📋"
          },
          {
            title: "Posture Analysis",
            content: "Analysis results are saved to the selected client's record.",
            type: "info",
            icon: "📸"
          },
          {
            title: "Treatment Plans",
            content: "Plans are created for the selected client.",
            type: "info",
            icon: "💪"
          }
        ]
      }
    ]
  },

  // Admin Parameter Help Content
  'posture-analysis-pelvic-tilt': {
    title: "Pelvic Tilt Classification Logic",
    description: "Understanding how pelvic tilt patterns are detected and classified",
    sections: [
      {
        title: "Overview",
        content: "The pelvic tilt classification system analyzes the angle of the pelvis to determine if it's in an anterior (forward) or posterior (backward) tilted position. This is crucial for identifying postural imbalances that can affect the entire kinetic chain.",
        type: "default"
      },
      {
        title: "Threshold Configuration",
        content: "The system uses configurable thresholds to classify pelvic tilt:",
        subsections: [
          {
            title: "Anterior Pelvic Tilt (APT) Threshold",
            content: "Degrees beyond which the pelvis is considered anteriorly tilted. Default: 6 degrees. Values above this threshold indicate the pelvis is rotated forward, often associated with tight hip flexors and weak glutes.",
            type: "info"
          },
          {
            title: "Posterior Pelvic Tilt (PPT) Threshold", 
            content: "Degrees below which the pelvis is considered posteriorly tilted. Default: -6 degrees. Values below this threshold indicate the pelvis is rotated backward, often associated with tight hamstrings and weak hip flexors.",
            type: "info"
          }
        ]
      },
      {
        title: "Clinical Significance",
        content: "Proper pelvic alignment is essential for optimal movement patterns. Misalignment can lead to compensatory patterns throughout the spine and lower extremities, affecting performance and increasing injury risk.",
        type: "technical"
      }
    ]
  },

  'posture-analysis-lateralization': {
    title: "Lateralization Detection Logic",
    description: "How the system detects left/right lateralization patterns in posture",
    sections: [
      {
        title: "Overview",
        content: "Lateralization detection identifies asymmetries between left and right sides of the body, which can indicate muscle imbalances, structural issues, or compensatory patterns.",
        type: "default"
      },
      {
        title: "Detection Method",
        content: "When both anterior and posterior captures exist, coronal metrics for lateralization (head tilt, shoulder level, hip level, pelvic shift) are taken from the **posterior** view first, because it reduces client posing bias and pairs with posterior-only foot metrics. The system compares bilateral measurements and applies configurable thresholds to determine if lateralization is present:",
        subsections: [
          {
            title: "Trunk Lean Lateralization Threshold",
            content: "Minimum difference in degrees between left and right trunk lean measurements to classify as lateralized. Default: 1 degree. This helps identify if the trunk is leaning more to one side.",
            type: "info"
          },
          {
            title: "Head Tilt Lateralization Threshold",
            content: "Minimum difference in degrees between left and right head tilt measurements. Default: 2 degrees. This detects if the head is consistently tilted to one side.",
            type: "info"
          }
        ]
      },
      {
        title: "Clinical Application",
        content: "Lateralization patterns often indicate underlying muscle imbalances or structural asymmetries that should be addressed through targeted exercises and interventions.",
        type: "technical"
      }
    ]
  },

  'posture-analysis-severity-bands': {
    title: "Severity Band Classification",
    description: "How posture deviations are classified into mild, moderate, and severe categories",
    sections: [
      {
        title: "Overview",
        content: "Severity bands provide a standardized way to categorize the extent of postural deviations, helping clinicians prioritize interventions and track progress over time.",
        type: "default"
      },
      {
        title: "Band Configuration",
        content: "Each measurement type has three severity thresholds:",
        subsections: [
          {
            title: "Mild (Green)",
            content: "Minor deviations that may not require immediate intervention but should be monitored. Typically 0-2 degrees for most measurements.",
            type: "success"
          },
          {
            title: "Moderate (Yellow)",
            content: "Noticeable deviations that may affect performance or comfort. Usually 2-5 degrees for most measurements.",
            type: "warning"
          },
          {
            title: "Severe (Red)",
            content: "Significant deviations that likely require intervention. Typically 5+ degrees for most measurements.",
            type: "warning"
          }
        ]
      },
      {
        title: "Customization",
        content: "Thresholds can be adjusted based on your clinical experience, client population, and specific assessment protocols. Consider your typical client demographics when setting these values.",
        type: "tip"
      }
    ]
  },

  'posture-analysis-ideal-values': {
    title: "Ideal Value Configuration",
    description: "Setting baseline ideal values for various postural measurements",
    sections: [
      {
        title: "Overview",
        content: "Ideal values serve as reference points for optimal posture. These values are used to calculate deviations and determine the extent of postural imbalances.",
        type: "default"
      },
      {
        title: "Key Measurements",
        content: "Common ideal values include:",
        subsections: [
          {
            title: "Knee Angle",
            content: "Ideal knee angle is typically 180 degrees (straight line from hip to ankle). Deviations indicate potential knee hyperextension or flexion.",
            type: "info"
          },
          {
            title: "Cervical Angle",
            content: "Ideal cervical angle varies by individual but typically ranges from 45-55 degrees. This represents the natural curve of the cervical spine.",
            type: "info"
          },
          {
            title: "Pelvic Angle",
            content: "Ideal pelvic angle is typically 0 degrees (neutral position). This represents a balanced pelvis without anterior or posterior tilt.",
            type: "info"
          }
        ]
      },
      {
        title: "Clinical Considerations",
        content: "Ideal values should be adjusted based on individual client characteristics, sport-specific requirements, and current research. Consider age, activity level, and specific performance goals when setting these values.",
        type: "technical"
      }
    ]
  },

  'posture-analysis-comprehensive-guide': {
    title: "Posture Analysis Logic - Complete Guide",
    description: "A comprehensive narrative explanation of how the posture analysis system works",
    sections: [
      {
        title: "The Foundation: Understanding Postural Assessment",
        content: "The posture analysis system evaluates the body's alignment and identifies deviations from optimal posture. It uses a combination of angular measurements, linear measurements, and pattern recognition to provide a comprehensive assessment of postural health.",
        type: "default"
      },
      {
        title: "Pelvic Tilt Classification System",
        content: "The system begins by analyzing pelvic position, which is crucial as it affects the entire kinetic chain:",
        type: "default",
        subsections: [
          {
            title: "Anterior Pelvic Tilt Detection",
            content: "When the pelvis tilts forward beyond 6 degrees, it's classified as anterior pelvic tilt. This often indicates tight hip flexors and weak glutes, leading to increased lumbar lordosis and potential lower back pain.",
            type: "info"
          },
          {
            title: "Swayback Pattern Recognition",
            content: "The system detects swayback posture by measuring hip forward displacement (2.5cm threshold), posterior shoulder shift relative to the hip, posterior pelvic line where available, and knee flexion over a small degree threshold (not hyperextension). This pattern indicates weak core muscles and posterior chain tightness.",
            type: "info"
          },
          {
            title: "Clinical Significance",
            content: "Pelvic misalignment affects the entire spine and can lead to compensatory patterns throughout the body, making it a critical assessment point.",
            type: "technical"
          }
        ]
      },
      {
        title: "Lateralization Detection",
        content: "The system identifies asymmetries between left and right sides of the body:",
        type: "default",
        subsections: [
          {
            title: "Trunk Lean Analysis",
            content: "Differences greater than 1 degree between left and right trunk lean indicate lateral deviation, often caused by muscle imbalances or structural asymmetries.",
            type: "info"
          },
          {
            title: "Shoulder Level Assessment",
            content: "Shoulder obliquity differences greater than 2 degrees suggest upper body asymmetries that may indicate scoliosis, muscle imbalances, or compensatory patterns.",
            type: "info"
          },
          {
            title: "Hip Level Evaluation",
            content: "Hip obliquity differences greater than 2 degrees indicate pelvic asymmetries that can affect gait and overall movement patterns.",
            type: "info"
          }
        ]
      },
      {
        title: "Severity Band Classification",
        content: "The system categorizes postural deviations into three severity levels to help prioritize interventions:",
        type: "default",
        subsections: [
          {
            title: "Mild Deviations (Green)",
            content: "Minor deviations that may not require immediate intervention but should be monitored. These are often within normal variation and may not cause symptoms.",
            type: "success"
          },
          {
            title: "Moderate Deviations (Yellow)",
            content: "Noticeable deviations that may affect performance or comfort. These often benefit from targeted exercises and postural awareness training.",
            type: "warning"
          },
          {
            title: "Severe Deviations (Red)",
            content: "Significant deviations that likely require intervention. These often cause symptoms and can lead to injury if not addressed.",
            type: "warning"
          }
        ]
      },
      {
        title: "Ideal Value Reference System",
        content: "The system uses ideal values as reference points for optimal posture:",
        type: "default",
        subsections: [
          {
            title: "Knee Angle (180°)",
            content: "Represents optimal knee alignment with no hyperextension or flexion. Deviations indicate potential joint issues or muscle imbalances.",
            type: "info"
          },
          {
            title: "Cervical Angle (50°)",
            content: "Represents the natural cervical lordosis. This curve is essential for proper head positioning and neck function.",
            type: "info"
          },
          {
            title: "Toe Out Angle (10°)",
            content: "Represents natural foot positioning during standing. This slight outward rotation is normal and functional.",
            type: "info"
          }
        ]
      },
      {
        title: "Clinical Application Process",
        content: "In practice, the posture analysis follows this workflow:",
        type: "default",
        subsections: [
          {
            title: "Step 1: Image Capture",
            content: "Standardized photographs are taken from multiple angles (anterior, posterior, lateral) to ensure consistent assessment.",
            type: "info"
          },
          {
            title: "Step 2: Measurement Analysis",
            content: "The system analyzes key anatomical landmarks and calculates angular and linear measurements.",
            type: "info"
          },
          {
            title: "Step 3: Pattern Recognition",
            content: "The system identifies common postural patterns and classifies deviations according to established criteria.",
            type: "info"
          },
          {
            title: "Step 4: Severity Assessment",
            content: "Deviations are categorized by severity to help prioritize interventions and track progress over time.",
            type: "info"
          }
        ]
      },
      {
        title: "Clinical Benefits",
        content: "This systematic approach provides objective, reproducible postural assessments that help clinicians identify issues early, track progress over time, and develop targeted intervention strategies. The configurable nature of the system allows for adaptation to different populations and evolving clinical standards.",
        type: "technical"
      }
    ]
  },

  'sop-assessment-test-nominals': {
    title: "Test Nominal Values Configuration",
    description: "Setting expected normal values for SOP assessment tests",
    sections: [
      {
        title: "Overview",
        content: "Test nominal values represent the expected normal range of motion or performance for each assessment test. These values are used to calculate performance percentages and identify limitations.",
        type: "default"
      },
      {
        title: "Configuration Process",
        content: "Each test requires a nominal value that represents optimal performance:",
        subsections: [
          {
            title: "Cervical Tests",
            content: "Cervical Axial Rotation and Lateral Flexion typically have nominal values around 30 degrees. These represent normal cervical spine mobility.",
            type: "info"
          },
          {
            title: "Shoulder Tests",
            content: "Shoulder Internal/External Rotation typically have nominal values around 90 degrees. Shoulder Abduction is typically around 60 degrees.",
            type: "info"
          },
          {
            title: "Hip Tests",
            content: "Hip Internal/External Rotation typically have nominal values around 40-45 degrees. Straight Leg Raise is typically around 80 degrees.",
            type: "info"
          }
        ]
      },
      {
        title: "Clinical Guidelines",
        content: "Nominal values should be based on current research and clinical guidelines. Consider your client population and specific assessment protocols when setting these values.",
        type: "technical"
      }
    ]
  },

  'sop-assessment-scoring-thresholds': {
    title: "Scoring Thresholds Configuration",
    description: "Setting performance level thresholds for SOP assessments",
    sections: [
      {
        title: "Overview",
        content: "Scoring thresholds define the percentage ranges for different performance levels, helping clinicians quickly assess and communicate test results.",
        type: "default"
      },
      {
        title: "Performance Levels",
        content: "The system uses four performance levels:",
        subsections: [
          {
            title: "Excellent (90-100%)",
            content: "Performance within or above normal range. No intervention typically needed.",
            type: "success"
          },
          {
            title: "Good (70-89%)",
            content: "Performance slightly below normal but within acceptable range. May benefit from maintenance exercises.",
            type: "info"
          },
          {
            title: "Fair (50-69%)",
            content: "Performance below normal range. Likely to benefit from targeted interventions.",
            type: "warning"
          },
          {
            title: "Poor (0-49%)",
            content: "Significantly below normal range. Requires immediate attention and intervention.",
            type: "warning"
          }
        ]
      },
      {
        title: "Customization",
        content: "Thresholds can be adjusted based on your clinical standards and client population. Consider the specific demands of your practice when setting these values.",
        type: "tip"
      }
    ]
  },

  'sop-assessment-analysis-settings': {
    title: "Analysis Settings Configuration",
    description: "Configuring how SOP assessment analysis is performed",
    sections: [
      {
        title: "Overview",
        content: "Analysis settings control how the system processes and interprets test results, including bilateral comparisons and overall assessment calculations.",
        type: "default"
      },
      {
        title: "Key Settings",
        content: "Important analysis parameters include:",
        subsections: [
          {
            title: "Analysis Threshold",
            content: "Minimum difference in degrees between left and right sides to consider as a bilateral asymmetry. Default: 5 degrees. This helps identify significant imbalances.",
            type: "info"
          },
          {
            title: "Bilateral Comparison",
            content: "When enabled, the system compares left and right side measurements to identify asymmetries that may indicate muscle imbalances or structural issues.",
            type: "info"
          }
        ]
      },
      {
        title: "Clinical Application",
        content: "These settings help ensure consistent and reliable assessment results. Adjust based on your clinical experience and the sensitivity required for your practice.",
        type: "technical"
      }
    ]
  },

  'sop-assessment-test-categories': {
    title: "Test Categories Organization",
    description: "Organizing tests into categories for better analysis and reporting",
    sections: [
      {
        title: "Overview",
        content: "Test categories help organize assessments into logical groups, making it easier to analyze patterns and generate comprehensive reports.",
        type: "default"
      },
      {
        title: "Default Categories",
        content: "The system includes several standard categories:",
        subsections: [
          {
            title: "Cervical",
            content: "Tests related to cervical spine mobility and function, including rotation and lateral flexion.",
            type: "info"
          },
          {
            title: "Shoulder",
            content: "Tests assessing shoulder mobility and stability, including internal/external rotation and abduction.",
            type: "info"
          },
          {
            title: "Pelvic",
            content: "Tests related to pelvic mobility and stability, including rotation and hip function.",
            type: "info"
          },
          {
            title: "Lower Extremity",
            content: "Tests assessing hip, knee, and ankle function, including range of motion and stability tests.",
            type: "info"
          }
        ]
      },
      {
        title: "Customization",
        content: "Categories can be modified to match your specific assessment protocols and clinical focus areas.",
        type: "tip"
      }
    ]
  },

  'sop-assessment-comprehensive-guide': {
    title: "SOP Assessment Logic - Complete Guide",
    description: "A comprehensive narrative explanation of how the SOP assessment system works",
    sections: [
      {
        title: "The Foundation: Test Nominal Values",
        content: "The SOP assessment system begins with a comprehensive set of expected normal values for each test. Think of these as the 'gold standard' measurements that represent optimal human movement and mobility.",
        type: "default",
        subsections: [
          {
            title: "Cervical Spine",
            content: "Both axial rotation and lateral flexion are expected to reach 30 degrees - this represents normal neck mobility that allows for comfortable head movement and good posture.",
            type: "info"
          },
          {
            title: "Shoulder Complex",
            content: "Internal and external rotation should both achieve 90 degrees, while abduction reaches 60 degrees. These values ensure the shoulder can move through its full functional range without restriction.",
            type: "info"
          },
          {
            title: "Pelvic Region",
            content: "Pelvic rotation is expected to reach 70 degrees, indicating good hip and lower back mobility that supports proper walking and movement patterns.",
            type: "info"
          },
          {
            title: "Lower Extremity",
            content: "Hip abduction should reach 45 degrees, straight leg raise 80 degrees, and femoral rotation 40-45 degrees - these values ensure the legs can move freely and support the body effectively.",
            type: "info"
          }
        ]
      },
      {
        title: "The Scoring System: Performance Levels",
        content: "Once measurements are taken, the system compares them against these nominal values using a percentage-based scoring system:",
        type: "default",
        subsections: [
          {
            title: "Excellent (100%)",
            content: "Performance at or above the expected normal range - no intervention needed, this represents optimal function.",
            type: "success"
          },
          {
            title: "Good (85%)",
            content: "Performance slightly below normal but still within an acceptable range - may benefit from maintenance exercises to prevent decline.",
            type: "info"
          },
          {
            title: "Fair (50%)",
            content: "Performance noticeably below normal range - likely to benefit from targeted interventions and exercises.",
            type: "warning"
          },
          {
            title: "Poor (0%)",
            content: "Significantly below normal range - requires immediate attention and intervention to address limitations.",
            type: "warning"
          }
        ]
      },
      {
        title: "The Analysis Engine: Detecting Patterns",
        content: "The system doesn't just look at individual scores - it analyzes patterns and relationships:",
        type: "default",
        subsections: [
          {
            title: "Bilateral Comparison",
            content: "It compares left and right sides, flagging differences greater than 5 degrees as potential asymmetries that may indicate muscle imbalances or structural issues.",
            type: "info"
          },
          {
            title: "Hypermobility Detection",
            content: "It identifies when someone exceeds 110% of normal range, which might indicate joint hypermobility that could lead to instability.",
            type: "info"
          },
          {
            title: "Category Analysis",
            content: "Tests are grouped into logical categories (Cervical, Shoulder, Pelvic, Lower Extremity, Foot) with different importance levels, allowing the system to prioritize which areas need the most attention.",
            type: "info"
          }
        ]
      },
      {
        title: "The Clinical Intelligence: Making Sense of Data",
        content: "The system goes beyond simple number crunching to provide clinical insights:",
        type: "default",
        subsections: [
          {
            title: "High-Importance Tests",
            content: "Certain tests like Cervical Axial Rotation, Shoulder Abduction, and Pelvic Rotation are marked as 'important' because they're particularly indicative of overall movement health.",
            type: "info"
          },
          {
            title: "Specialized Assessments",
            content: "Hip Extension uses numeric degrees short of full extension (0° = full); Grounding Performance supports both dynamometer (default) and perceived (1-10) scoring via mode toggle, with perceived as a fallback when dynamometer is unavailable.",
            type: "info"
          },
          {
            title: "Contextual Understanding",
            content: "The system understands that foot presentation and grounding performance are more about quality of movement than specific measurements.",
            type: "info"
          }
        ]
      },
      {
        title: "The Practical Application: Real-World Use",
        content: "In practice, this means when you perform an SOP assessment:",
        type: "default",
        subsections: [
          {
            title: "Step 1: Measurement",
            content: "You measure each test according to standard protocols",
            type: "info"
          },
          {
            title: "Step 2: Calculation",
            content: "The system calculates how each measurement compares to the expected normal values",
            type: "info"
          },
          {
            title: "Step 3: Analysis",
            content: "It identifies patterns, asymmetries, and areas of concern",
            type: "info"
          },
          {
            title: "Step 4: Insights",
            content: "It provides a comprehensive analysis that helps you understand not just what's limited, but why it might be limited and what to do about it",
            type: "info"
          }
        ]
      },
      {
        title: "Clinical Benefits",
        content: "This creates a data-driven, evidence-based approach to movement assessment that's both scientifically rigorous and practically useful for clinical decision-making. The beauty of this system is that all these parameters can be adjusted by administrators based on your specific clinical experience, client population, or evolving research - making it a truly adaptive tool that grows with your practice.",
        type: "technical"
      }
    ]
  },

  'drill-library-filter-logic': {
    title: "Filter Logic Configuration",
    description: "Setting up how filters are applied and combined in the drill library",
    sections: [
      {
        title: "Overview",
        content: "Filter logic determines how multiple filters are combined when searching for drills. This affects how specific or broad the search results will be.",
        type: "default"
      },
      {
        title: "Filter Options",
        content: "Two main filter logic options are available:",
        subsections: [
          {
            title: "AND Logic",
            content: "All selected filters must match for a drill to appear in results. This creates more specific, narrower search results.",
            type: "info"
          },
          {
            title: "OR Logic",
            content: "Any selected filter can match for a drill to appear in results. This creates broader, more inclusive search results.",
            type: "info"
          }
        ]
      },
      {
        title: "Clinical Application",
        content: "AND logic is useful when you need very specific drills for a particular condition. OR logic is better for exploratory searches or when you want to see a wide range of options.",
        type: "tip"
      }
    ]
  },

  'drill-library-search-settings': {
    title: "Search and Sorting Configuration",
    description: "Configuring how drills are searched and sorted in the library",
    sections: [
      {
        title: "Overview",
        content: "Search settings control which fields are searched and how results are sorted, affecting the user experience and drill discovery.",
        type: "default"
      },
      {
        title: "Search Fields",
        content: "Configure which fields are included in text searches:",
        subsections: [
          {
            title: "Name",
            content: "Search drill names and titles. Essential for finding specific drills by name.",
            type: "info"
          },
          {
            title: "Description",
            content: "Search drill descriptions and instructions. Important for finding drills by their purpose or method.",
            type: "info"
          },
          {
            title: "Tags",
            content: "Search drill tags and categories. Crucial for finding drills by specific attributes or classifications.",
            type: "info"
          },
          {
            title: "Transcript",
            content: "Search video transcripts and detailed instructions. Useful for finding drills by specific techniques or movements.",
            type: "info"
          }
        ]
      },
      {
        title: "Sorting Options",
        content: "Results can be sorted by relevance, name, date added, or other criteria. Choose the sorting method that best serves your workflow.",
        type: "tip"
      }
    ]
  },

  'drill-library-display-settings': {
    title: "Display Settings Configuration",
    description: "Configuring how drills are displayed and organized in the library",
    sections: [
      {
        title: "Overview",
        content: "Display settings control the visual presentation and organization of drills, affecting how users browse and interact with the library.",
        type: "default"
      },
      {
        title: "Key Settings",
        content: "Important display parameters include:",
        subsections: [
          {
            title: "Items Per Page",
            content: "Number of drills displayed per page. Balance between performance and usability. Default: 12 items.",
            type: "info"
          },
          {
            title: "View Modes",
            content: "Available view modes include grid, list, and card views. Each offers different levels of detail and visual appeal.",
            type: "info"
          },
          {
            title: "Tag Priorities",
            content: "Order of importance for drill tags, affecting how they're displayed and used for filtering.",
            type: "info"
          }
        ]
      },
      {
        title: "User Experience",
        content: "Display settings should be optimized for your typical use cases. Consider the screen sizes and devices your users typically employ.",
        type: "tip"
      }
    ]
  },

  'drill-library-categorization-rules': {
    title: "Categorization Rules Configuration",
    description: "Defining rules for how drills are categorized and organized",
    sections: [
      {
        title: "Overview",
        content: "Categorization rules define how drills are automatically classified and organized, making it easier to find and manage drill collections.",
        type: "default"
      },
      {
        title: "Rule Types",
        content: "Common categorization rules include:",
        subsections: [
          {
            title: "Phase-Based",
            content: "Categorize drills by training phase (Warm-up, Activation, Strength, Endurance, Cool-down).",
            type: "info"
          },
          {
            title: "Domain-Based",
            content: "Categorize drills by body region (Upper Body, Lower Body, Core, Full Body).",
            type: "info"
          },
          {
            title: "Movement Pattern",
            content: "Categorize drills by movement type (Push, Pull, Squat, Hinge, Lunge, Rotational).",
            type: "info"
          },
          {
            title: "Biomechanical Intent",
            content: "Categorize drills by their biomechanical purpose (Mobility, Stability, Strength, Power, Endurance).",
            type: "info"
          }
        ]
      },
      {
        title: "Customization",
        content: "Categorization rules can be customized to match your specific training philosophy and assessment protocols. Consider your client population and training goals when setting these rules.",
        type: "technical"
      }
    ]
  },

  'drill-library-comprehensive-guide': {
    title: "Drill Library Logic - Complete Guide",
    description: "A comprehensive narrative explanation of how the drill library system works",
    sections: [
      {
        title: "The Foundation: Intelligent Drill Management",
        content: "The drill library system serves as the central repository for all therapeutic exercises and movement interventions. It uses sophisticated filtering, searching, and categorization logic to help clinicians quickly find the most appropriate drills for their clients' specific needs.",
        type: "default"
      },
      {
        title: "Filter Logic: Finding the Right Drills",
        content: "The system uses intelligent filtering to narrow down drill selections:",
        type: "default",
        subsections: [
          {
            title: "AND Logic (Precise Matching)",
            content: "When you need very specific drills, AND logic ensures all selected filters must match. This is perfect for finding drills that address multiple specific criteria simultaneously.",
            type: "info"
          },
          {
            title: "OR Logic (Broad Discovery)",
            content: "When exploring options or wanting to see a wide range of possibilities, OR logic shows drills that match any of the selected criteria, expanding your options.",
            type: "info"
          },
          {
            title: "Priority Categories",
            content: "Certain categories like 'Phase' and 'Domain' take priority in filtering, ensuring the most important organizational criteria are considered first.",
            type: "info"
          }
        ]
      },
      {
        title: "Search and Discovery System",
        content: "The search functionality helps you find drills through multiple pathways:",
        type: "default",
        subsections: [
          {
            title: "Multi-Field Search",
            content: "The system searches across drill names, descriptions, and transcripts simultaneously, ensuring you find relevant drills regardless of how they're described.",
            type: "info"
          },
          {
            title: "Fuzzy Matching",
            content: "Even if you don't spell something perfectly, the system can find relevant drills through intelligent pattern matching.",
            type: "info"
          },
          {
            title: "Case-Insensitive Search",
            content: "Search works regardless of capitalization, making it user-friendly and forgiving of different input styles.",
            type: "info"
          }
        ]
      },
      {
        title: "Display and Organization",
        content: "The system organizes drills for optimal usability:",
        type: "default",
        subsections: [
          {
            title: "Tag Priority System",
            content: "Tags are displayed in order of importance: Phase, Domain, Movement Pattern, and Biomechanical Intent. This hierarchy helps you quickly understand each drill's purpose.",
            type: "info"
          },
          {
            title: "Flexible Sorting",
            content: "Drills can be sorted by name (A-Z or Z-A) or by date (newest or oldest), allowing you to organize your view based on your current needs.",
            type: "info"
          },
          {
            title: "Selective Display",
            content: "Only the most relevant tags are shown on drill cards to avoid clutter while maintaining essential information visibility.",
            type: "info"
          }
        ]
      },
      {
        title: "Categorization Intelligence",
        content: "The system automatically categorizes drills based on their characteristics:",
        type: "default",
        subsections: [
          {
            title: "Mobility Drills",
            content: "Automatically identified by 'Mobility' tags, these drills focus on improving range of motion and flexibility. They exclude strength-focused exercises to maintain focus.",
            type: "info"
          },
          {
            title: "Stability Drills",
            content: "Identified by 'Stability' tags, these exercises focus on core strength and balance. They're separate from mobility work to ensure appropriate programming.",
            type: "info"
          },
          {
            title: "Strength Drills",
            content: "Marked by 'Strength' tags, these exercises focus on building muscular strength and power. They're distinct from mobility work to ensure proper training progression.",
            type: "info"
          }
        ]
      },
      {
        title: "Clinical Application Workflow",
        content: "In practice, the drill library supports this workflow:",
        type: "default",
        subsections: [
          {
            title: "Step 1: Assessment Analysis",
            content: "After completing assessments, you identify the areas that need attention and the type of intervention required.",
            type: "info"
          },
          {
            title: "Step 2: Drill Discovery",
            content: "Use filters and search to find drills that address the specific issues identified in your assessment.",
            type: "info"
          },
          {
            title: "Step 3: Program Building",
            content: "Select appropriate drills and organize them into a coherent program that progresses logically.",
            type: "info"
          },
          {
            title: "Step 4: Client Education",
            content: "Use the drill information to educate clients about their exercises and ensure proper execution.",
            type: "info"
          }
        ]
      },
      {
        title: "Clinical Benefits",
        content: "This intelligent system saves time, ensures consistency, and helps clinicians provide evidence-based interventions. The configurable nature allows for adaptation to different specialties, client populations, and evolving therapeutic approaches.",
        type: "technical"
      }
    ]
  },

  drillPlanner: {
    title: "Drill Planner Help",
    description: "Create and manage personalized intervention plans for your clients",
    sections: [
      {
        title: "Overview",
        content: "The Drill Planner allows you to create customized multi-day intervention programs by combining drills from your library. Build programs tailored to each client's specific needs and progression stage.",
      },
      {
        title: "Getting Started",
        content: "Select a client first using the Client Toolbar, then you can create, load, or modify their intervention programs.",
        subsections: [
          {
            title: "Select Client",
            content: "Use the Client Toolbar at the top of the page to select which client you want to work with.",
            type: "info",
            icon: "👤"
          },
          {
            title: "Create New Program",
            content: "Click the 'New Program' button to start building a fresh intervention plan for the selected client.",
            type: "info",
            icon: "➕"
          }
        ]
      },
      {
        title: "Program Structure",
        content: "Organize your programs into logical days with customizable exercise slots:",
        subsections: [
          {
            title: "Days",
            content: "Add multiple days to your program (e.g., Day 1, Day 2, etc.). Each day can be named differently (e.g., 'Upper Body', 'Lower Body', 'Full Body').",
            type: "info",
            icon: "📅"
          },
          {
            title: "Exercise Slots",
            content: "Each day has up to 6 exercise slots (A1-C2) with suggested purposes. You can customize the slot labels to match your terminology.",
            type: "info",
            icon: "🎯"
          },
          {
            title: "Slot Purposes",
            content: "A1: Main Lower Push, A2: Main Upper Pull, B1: Accessory Upper Push, B2: Accessory Lower Pull, C1: Core, C2: General Accessory",
            type: "info",
            icon: "📋"
          }
        ]
      },
      {
        title: "Adding Drills",
        content: "Use drag-and-drop to add drills from the library to your program slots.",
        subsections: [
          {
            title: "Drag from Library",
            content: "The left side shows available drills from your library. Simply drag any drill to an empty slot in your program.",
            type: "info",
            icon: "🎬"
          },
          {
            title: "Search Drills",
            content: "Use the search box in the library to find specific drills by name or category.",
            type: "info",
            icon: "🔍"
          },
          {
            title: "Configure Parameters",
            content: "For each drill in a slot, set Sets, Reps, Rest period, Tempo, and any special Notes for execution.",
            type: "tip",
            icon: "⚙️"
          }
        ]
      },
      {
        title: "Program Templates",
        content: "Quickly create program structures using pre-built templates:",
        subsections: [
          {
            title: "Push/Pull/Legs",
            content: "Classic 3-day split: Push Day, Pull Day, and Legs Day. Perfect for comprehensive training.",
            type: "info",
            icon: "💪"
          },
          {
            title: "Upper/Lower",
            content: "2-day split focusing on Upper Body and Lower Body training for balanced development.",
            type: "info",
            icon: "🔄"
          },
          {
            title: "Full Body",
            content: "Single day program covering all movement patterns for time-efficient training.",
            type: "info",
            icon: "🎯"
          }
        ]
      },
      {
        title: "Program Management",
        content: "Save, load, and manage your intervention programs:",
        subsections: [
          {
            title: "Save",
            content: "Click 'Save' to save your current program to the database.",
            type: "info",
            icon: "💾"
          },
          {
            title: "Program History",
            content: "View all previously saved programs for the client. Click to load any previous version or create a new one from it.",
            type: "info",
            icon: "📜"
          },
          {
            title: "Import/Export",
            content: "Download programs as JSON files for backup or sharing. Import previously exported programs.",
            type: "tip",
            icon: "📤"
          },
          {
            title: "Delete Programs",
            content: "Remove old or no longer needed programs from your records.",
            type: "info",
            icon: "🗑️"
          }
        ]
      },
      {
        title: "Best Practices",
        content: "Tips for creating effective intervention programs:",
        subsections: [
          {
            title: "Progressive Overload",
            content: "Start conservatively and gradually increase sets, reps, or weight. Modify programs regularly based on client progress.",
            type: "tip",
            icon: "📈"
          },
          {
            title: "Movement Variety",
            content: "Include different movement patterns and body regions to ensure balanced development and prevent compensatory patterns.",
            type: "tip",
            icon: "🔀"
          },
          {
            title: "Recovery",
            content: "Balance training intensity with adequate rest periods and recovery days. Tailor programs to client's current state.",
            type: "tip",
            icon: "😴"
          },
          {
            title: "Documentation",
            content: "Use notes in each slot to document cues, modifications, or special considerations for each drill.",
            type: "tip",
            icon: "📝"
          }
        ]
      }
    ]
  }
};

/**
 * Get help content for a specific page or component
 */
export function getHelpContent(key: string): HelpContent | undefined {
  return HELP_CONTENT[key];
}

/**
 * Check if help content exists for a key
 */
export function hasHelpContent(key: string): boolean {
  return key in HELP_CONTENT;
}

